import React, { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import useSWR from 'swr'
import { fetcher } from '../../../swr-config'
import { getMatchupColors } from '../../../utils/teamColors'
import { getLeagueBadge } from '../../../utils/leagueBadge'
import { PlayerProfileModal } from '../../PlayerProfileModal'
import type { HistoricalModalTab, HistoricalFixtureDetail } from '../types'
import type { FixtureEvent, LiveStats, H2HStatsResponse, H2HFilter, H2HMatchStatsOut } from '../../../types'
import '../styles.css'

interface HistoricalFixtureModalProps {
  fixtureId: number
  onClose: () => void
  onFixtureChange?: (newFixtureId: number) => void
}

type ModalTab = HistoricalModalTab

const MODAL_TABS: { id: ModalTab; label: string }[] = [
  { id: 'facts', label: 'Facts' },
  { id: 'lineup', label: 'Lineup' },
  { id: 'stats', label: 'Stats' },
  { id: 'h2h', label: 'H2H' },
]

function ModalTabNav({ activeTab, onTabChange }: { activeTab: ModalTab; onTabChange: (tab: ModalTab) => void }) {
  return (
    <div className="tabs" style={{ borderBottom: '1px solid var(--border)', marginBottom: '1rem' }}>
      {MODAL_TABS.map((tab) => (
        <button
          key={tab.id}
          className={`tab ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => onTabChange(tab.id)}
          type="button"
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

function formatGoalMinute(elapsed: number, elapsed_extra: number | null | undefined): string {
  return elapsed_extra ? `${elapsed}+${elapsed_extra}'` : `${elapsed}'`
}

/**
 * Clickable player name component.
 * Only renders as clickable if player_id is available.
 */
function ClickablePlayer({
  playerId,
  playerName,
  onClick,
  style,
}: {
  playerId?: number | null
  playerName?: string | null
  onClick: (playerId: number) => void
  style?: React.CSSProperties
}) {
  if (!playerName) return null

  if (!playerId) {
    // No player_id available, render as non-clickable
    return <span style={style}>{playerName}</span>
  }

  return (
    <span
      style={{
        ...style,
        cursor: 'pointer',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textUnderlineOffset: '2px',
      }}
      onClick={(e) => {
        e.stopPropagation()
        onClick(playerId)
      }}
      title="Click to view player stats"
    >
      {playerName}
    </span>
  )
}

function FactsTab({ data, onPlayerClick }: { data: HistoricalFixtureDetail; onPlayerClick: (playerId: number) => void }) {
  // Separate timeline events (with known time) from events without time (elapsed = -1)
  const timedEvents = data.events
    .filter((e) => ['Goal', 'Card', 'subst', 'Var'].includes(e.event_type))
    .filter((e) => e.elapsed >= 0)
    .sort((a, b) => {
      const timeA = a.elapsed + (a.elapsed_extra || 0) / 100
      const timeB = b.elapsed + (b.elapsed_extra || 0) / 100
      return timeA - timeB
    })

  // Missed penalties without known minute (from player stats)
  const untimedMissedPenalties = data.events.filter(
    (e) => e.event_type === 'Goal' && e.detail?.toLowerCase().includes('missed') && e.elapsed < 0
  )

  // Identify second yellow cards by counting yellows per player
  // A player's 2nd yellow card results in a red (shown as 🟨🟥)
  const yellowCardsByPlayer = new Map<string, Array<{ elapsed: number; elapsed_extra: number | null }>>()

  // Collect all yellow cards per player (sorted by time)
  timedEvents.forEach((e) => {
    if (e.event_type === 'Card' && e.detail?.includes('Yellow') && e.player_name) {
      const yellows = yellowCardsByPlayer.get(e.player_name) || []
      yellows.push({ elapsed: e.elapsed, elapsed_extra: e.elapsed_extra })
      yellowCardsByPlayer.set(e.player_name, yellows)
    }
  })

  // Build a set of "second yellow" event keys (player + time)
  const secondYellowKeys = new Set<string>()
  yellowCardsByPlayer.forEach((yellows, playerName) => {
    if (yellows.length >= 2) {
      // Sort by time to find the second one
      yellows.sort((a, b) => {
        const timeA = a.elapsed + (a.elapsed_extra || 0) / 100
        const timeB = b.elapsed + (b.elapsed_extra || 0) / 100
        return timeA - timeB
      })
      // Mark the second yellow
      const second = yellows[1]
      const key = `${playerName}-${second.elapsed}-${second.elapsed_extra || 0}`
      secondYellowKeys.add(key)
    }
  })

  // No need to filter events - we just mark second yellows with special icon
  const timelineEvents = timedEvents

  // Helper to check if a yellow card is actually a second yellow
  const isSecondYellow = (event: typeof timelineEvents[0]) => {
    if (event.event_type === 'Card' && event.detail?.includes('Yellow') && event.player_name) {
      const key = `${event.player_name}-${event.elapsed}-${event.elapsed_extra || 0}`
      return secondYellowKeys.has(key)
    }
    return false
  }

  const isMissedPenalty = (event: typeof timelineEvents[0]) =>
    event.event_type === 'Goal' && event.detail?.toLowerCase().includes('missed')

  const getEventIcon = (event: typeof timelineEvents[0]) => {
    if (event.event_type === 'Goal') {
      if (isMissedPenalty(event)) return '❌'
      return '⚽'
    }
    if (event.event_type === 'Card') {
      if (isSecondYellow(event)) return '🟨🟥'
      return event.detail?.includes('Red') ? '🟥' : '🟨'
    }
    if (event.event_type === 'Var') {
      return '📺'
    }
    return null
  }

  const getEventColor = (event: typeof timelineEvents[0]) => {
    if (event.event_type === 'Goal') {
      if (isMissedPenalty(event)) return 'var(--red)'
      return 'var(--green)'
    }
    if (event.event_type === 'Card') {
      // Second yellow (which results in red) - use red color
      if (isSecondYellow(event)) return '#ef4444'
      return event.detail?.includes('Red') ? '#ef4444' : '#f59e0b'
    }
    if (event.event_type === 'Var') {
      return 'var(--accent)'
    }
    return 'var(--muted)'
  }

  return (
    <div>
      {/* Score header */}
      <div style={{ textAlign: 'center', marginBottom: '1rem', padding: '1rem', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {data.home_team.crest_url && <img src={data.home_team.crest_url} alt="" style={{ width: 32, height: 32 }} />}
            <span style={{ fontWeight: 600 }}>{data.home_team.name}</span>
          </div>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent)' }}>
            {data.home_score} - {data.away_score}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontWeight: 600 }}>{data.away_team.name}</span>
            {data.away_team.crest_url && <img src={data.away_team.crest_url} alt="" style={{ width: 32, height: 32 }} />}
          </div>
        </div>
        {data.home_score_ht !== null && data.away_score_ht !== null && (
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
            HT: {data.home_score_ht} - {data.away_score_ht}
          </div>
        )}
      </div>

      {/* Match info */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginBottom: '1rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
        <span>{new Date(data.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
        {data.venue && <span>{data.venue}</span>}
        {data.referee && <span>Ref: {data.referee}</span>}
      </div>

      {/* Added time display */}
      {(data.added_time_1h !== null || data.added_time_2h !== null) && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '1.5rem',
          marginBottom: '1rem',
          fontSize: '0.6875rem',
          color: 'var(--muted)',
        }}>
          {data.added_time_1h !== null && (
            <span>1st Half: +{data.added_time_1h} mins</span>
          )}
          {data.added_time_2h !== null && (
            <span>2nd Half: +{data.added_time_2h} mins</span>
          )}
        </div>
      )}

      {/* Event Timeline */}
      {timelineEvents.length > 0 && (
        <div style={{ position: 'relative', padding: '0.5rem 0' }}>
          {/* Center line */}
          <div style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: 2,
            background: 'var(--border)',
            transform: 'translateX(-50%)',
          }} />

          {timelineEvents.map((event, i) => {
            const isHome = event.team_id === data.home_team.id
            const minute = formatGoalMinute(event.elapsed, event.elapsed_extra)

            // Check if we need to show HT marker before this event
            const prevEvent = i > 0 ? timelineEvents[i - 1] : null
            const showHT = event.elapsed > 45 && (!prevEvent || prevEvent.elapsed <= 45)

            return (
              <React.Fragment key={i}>
                {showHT && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '0.75rem',
                      position: 'relative',
                    }}
                  >
                    <div style={{ flex: 1 }} />
                    <div style={{
                      width: 40,
                      textAlign: 'center',
                      fontSize: '0.6875rem',
                      color: 'var(--accent)',
                      fontWeight: 700,
                      background: 'var(--bg)',
                      position: 'relative',
                      zIndex: 1,
                      padding: '0.25rem 0',
                    }}>
                      HT
                    </div>
                    <div style={{ flex: 1 }} />
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '0.75rem',
                    position: 'relative',
                  }}
                >
                {/* Home team event (left side) */}
                <div style={{
                  flex: 1,
                  textAlign: 'right',
                  paddingRight: '1.5rem',
                  opacity: isHome ? 1 : 0,
                }}>
                  {isHome && (
                    <div>
                      <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                        {getEventIcon(event) && <span style={{ marginRight: '0.5rem' }}>{getEventIcon(event)}</span>}
                        {event.event_type === 'subst' ? (
                          <span style={{ color: 'var(--muted)' }}>
                            Sub: <ClickablePlayer playerId={event.player_id} playerName={event.player_name} onClick={onPlayerClick} /> <span style={{ color: 'var(--red)' }}>↓</span> <ClickablePlayer playerId={event.assist_player_id} playerName={event.assist_player_name} onClick={onPlayerClick} /> <span style={{ color: 'var(--green)' }}>↑</span>
                          </span>
                        ) : event.event_type === 'Var' ? (
                          <span style={{ color: getEventColor(event) }}>
                            VAR: {event.detail || 'Decision'}
                          </span>
                        ) : (
                          <ClickablePlayer playerId={event.player_id} playerName={event.player_name} onClick={onPlayerClick} style={{ color: getEventColor(event) }} />
                        )}
                        {event.event_type === 'Goal' && event.detail === 'Own Goal' && (
                          <span style={{ color: 'var(--red)', fontSize: '0.75rem' }}> (OG)</span>
                        )}
                        {event.event_type === 'Goal' && event.detail === 'Penalty' && (
                          <span style={{ color: 'var(--accent)', fontSize: '0.75rem' }}> (P)</span>
                        )}
                        {isMissedPenalty(event) && (
                          <span style={{ color: 'var(--red)', fontSize: '0.75rem' }}> (Missed Pen)</span>
                        )}
                      </div>
                      {event.event_type === 'Goal' && !isMissedPenalty(event) && event.assist_player_name && (
                        <div style={{ fontSize: '0.6875rem', color: 'var(--muted)', fontWeight: 400 }}>
                          Assist: <ClickablePlayer playerId={event.assist_player_id} playerName={event.assist_player_name} onClick={onPlayerClick} />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Center minute marker */}
                <div style={{
                  width: 40,
                  textAlign: 'center',
                  fontSize: '0.6875rem',
                  color: 'var(--muted)',
                  fontWeight: 600,
                  background: 'var(--bg)',
                  position: 'relative',
                  zIndex: 1,
                }}>
                  {minute}
                </div>

                {/* Away team event (right side) */}
                <div style={{
                  flex: 1,
                  textAlign: 'left',
                  paddingLeft: '1.5rem',
                  opacity: !isHome ? 1 : 0,
                }}>
                  {!isHome && (
                    <div>
                      <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                        {event.event_type === 'subst' ? (
                          <span style={{ color: 'var(--muted)' }}>
                            Sub: <ClickablePlayer playerId={event.player_id} playerName={event.player_name} onClick={onPlayerClick} /> <span style={{ color: 'var(--red)' }}>↓</span> <ClickablePlayer playerId={event.assist_player_id} playerName={event.assist_player_name} onClick={onPlayerClick} /> <span style={{ color: 'var(--green)' }}>↑</span>
                          </span>
                        ) : event.event_type === 'Var' ? (
                          <span style={{ color: getEventColor(event) }}>
                            VAR: {event.detail || 'Decision'}
                          </span>
                        ) : (
                          <ClickablePlayer playerId={event.player_id} playerName={event.player_name} onClick={onPlayerClick} style={{ color: getEventColor(event) }} />
                        )}
                        {event.event_type === 'Goal' && event.detail === 'Own Goal' && (
                          <span style={{ color: 'var(--red)', fontSize: '0.75rem' }}> (OG)</span>
                        )}
                        {event.event_type === 'Goal' && event.detail === 'Penalty' && (
                          <span style={{ color: 'var(--accent)', fontSize: '0.75rem' }}> (P)</span>
                        )}
                        {isMissedPenalty(event) && (
                          <span style={{ color: 'var(--red)', fontSize: '0.75rem' }}> (Missed Pen)</span>
                        )}
                        {getEventIcon(event) && <span style={{ marginLeft: '0.5rem' }}>{getEventIcon(event)}</span>}
                      </div>
                      {event.event_type === 'Goal' && !isMissedPenalty(event) && event.assist_player_name && (
                        <div style={{ fontSize: '0.6875rem', color: 'var(--muted)', fontWeight: 400 }}>
                          Assist: <ClickablePlayer playerId={event.assist_player_id} playerName={event.assist_player_name} onClick={onPlayerClick} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              </React.Fragment>
            )
          })}
        </div>
      )}

      {/* Missed penalties without known minute (from player stats) */}
      {untimedMissedPenalties.length > 0 && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.5rem 0.75rem',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: 6,
          border: '1px solid rgba(239, 68, 68, 0.2)',
        }}>
          <div style={{ fontSize: '0.6875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
            Missed Penalties
          </div>
          {untimedMissedPenalties.map((event, i) => {
            const isHome = event.team_id === data.home_team.id
            return (
              <div key={i} style={{ fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>❌</span>
                <ClickablePlayer playerId={event.player_id} playerName={event.player_name} onClick={onPlayerClick} style={{ color: 'var(--red)' }} />
                <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>
                  ({isHome ? data.home_team.name : data.away_team.name})
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Key stats summary */}
      {data.home_stats && data.away_stats && (
        <div className="live-stats" style={{ marginTop: '1rem' }}>
          <StatRow label="Possession" home={data.home_stats.possession} away={data.away_stats.possession} suffix="%" />
          <StatRow label="Shots" home={data.home_stats.shots} away={data.away_stats.shots} />
          <StatRow label="On Target" home={data.home_stats.shots_on_target} away={data.away_stats.shots_on_target} />
          <StatRow label="Corners" home={data.home_stats.corners} away={data.away_stats.corners} />
          <StatRow label="Fouls" home={data.home_stats.fouls} away={data.away_stats.fouls} />
        </div>
      )}
    </div>
  )
}

function LineupTab({ data, onPlayerClick }: { data: HistoricalFixtureDetail; onPlayerClick: (playerId: number) => void }) {
  if (!data.home_xi || data.home_xi.length === 0) {
    return <div className="ma-empty">Lineup data not available for this match</div>
  }

  // Combine and sort subs by shirt number
  const homeSubs = [...data.home_subs_used, ...(data.home_subs_unused || [])]
    .sort((a, b) => (a.number || 99) - (b.number || 99))
  const awaySubs = [...data.away_subs_used, ...(data.away_subs_unused || [])]
    .sort((a, b) => (a.number || 99) - (b.number || 99))

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
      <div>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {data.home_team.crest_url && <img src={data.home_team.crest_url} alt="" style={{ width: 20, height: 20 }} />}
          {data.home_team.name}
        </div>
        <div style={{ fontSize: '0.8125rem' }}>
          {data.home_xi.map((p) => (
            <div key={p.player_id} style={{ padding: '0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {p.number && <span style={{ color: 'var(--muted)', minWidth: 20 }}>{p.number}</span>}
              <ClickablePlayer playerId={p.player_id} playerName={p.name} onClick={onPlayerClick} />
              {p.goals > 0 && <span style={{ color: 'var(--green)' }}>⚽{p.goals > 1 ? `×${p.goals}` : ''}</span>}
              {p.assists > 0 && <span style={{ color: 'var(--accent)' }}>👟{p.assists > 1 ? `×${p.assists}` : ''}</span>}
              {p.yellow_cards > 0 && <span>🟨</span>}
              {p.red_card && <span>🟥</span>}
              {p.substituted_out && <span style={{ color: 'var(--red)', fontSize: '0.6875rem' }}>↓ {p.substituted_out}'</span>}
            </div>
          ))}
        </div>
        {homeSubs.length > 0 && (
          <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Substitutes</div>
            {homeSubs.map((p) => (
              <div key={p.player_id} style={{ fontSize: '0.75rem', padding: '0.125rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: p.substituted_in ? 1 : 0.6 }}>
                {p.number && <span style={{ color: 'var(--muted)', minWidth: 20 }}>{p.number}</span>}
                <ClickablePlayer playerId={p.player_id} playerName={p.name} onClick={onPlayerClick} />
                {p.substituted_in && <span style={{ color: 'var(--green)', fontSize: '0.6875rem' }}>↑ {p.substituted_in}'</span>}
                {p.goals > 0 && <span style={{ color: 'var(--green)' }}>⚽{p.goals > 1 ? `×${p.goals}` : ''}</span>}
                {p.assists > 0 && <span style={{ color: 'var(--accent)' }}>👟{p.assists > 1 ? `×${p.assists}` : ''}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {data.away_team.crest_url && <img src={data.away_team.crest_url} alt="" style={{ width: 20, height: 20 }} />}
          {data.away_team.name}
        </div>
        <div style={{ fontSize: '0.8125rem' }}>
          {data.away_xi.map((p) => (
            <div key={p.player_id} style={{ padding: '0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {p.number && <span style={{ color: 'var(--muted)', minWidth: 20 }}>{p.number}</span>}
              <ClickablePlayer playerId={p.player_id} playerName={p.name} onClick={onPlayerClick} />
              {p.goals > 0 && <span style={{ color: 'var(--green)' }}>⚽{p.goals > 1 ? `×${p.goals}` : ''}</span>}
              {p.assists > 0 && <span style={{ color: 'var(--accent)' }}>👟{p.assists > 1 ? `×${p.assists}` : ''}</span>}
              {p.yellow_cards > 0 && <span>🟨</span>}
              {p.red_card && <span>🟥</span>}
              {p.substituted_out && <span style={{ color: 'var(--red)', fontSize: '0.6875rem' }}>↓ {p.substituted_out}'</span>}
            </div>
          ))}
        </div>
        {awaySubs.length > 0 && (
          <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Substitutes</div>
            {awaySubs.map((p) => (
              <div key={p.player_id} style={{ fontSize: '0.75rem', padding: '0.125rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: p.substituted_in ? 1 : 0.6 }}>
                {p.number && <span style={{ color: 'var(--muted)', minWidth: 20 }}>{p.number}</span>}
                <ClickablePlayer playerId={p.player_id} playerName={p.name} onClick={onPlayerClick} />
                {p.substituted_in && <span style={{ color: 'var(--green)', fontSize: '0.6875rem' }}>↑ {p.substituted_in}'</span>}
                {p.goals > 0 && <span style={{ color: 'var(--green)' }}>⚽{p.goals > 1 ? `×${p.goals}` : ''}</span>}
                {p.assists > 0 && <span style={{ color: 'var(--accent)' }}>👟{p.assists > 1 ? `×${p.assists}` : ''}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function StatsTab({ data }: { data: HistoricalFixtureDetail }) {
  if (!data.home_stats || !data.away_stats) {
    return <div className="ma-empty">Stats not available for this match</div>
  }

  const { homeColor, awayColor } = getMatchupColors(data.home_team.name, data.away_team.name)

  const stats: { label: string; home: number | null | undefined; away: number | null | undefined; suffix?: string; decimal?: boolean }[] = [
    { label: 'Ball Possession', home: data.home_stats.possession, away: data.away_stats.possession, suffix: '%' },
  ]

  // Add xG if available
  if (data.home_stats.xg != null && data.away_stats.xg != null) {
    stats.push({ label: 'Expected Goals (xG)', home: data.home_stats.xg, away: data.away_stats.xg, decimal: true })
  }

  stats.push(
    { label: 'Total Shots', home: data.home_stats.shots, away: data.away_stats.shots },
    { label: 'Shots on Target', home: data.home_stats.shots_on_target, away: data.away_stats.shots_on_target },
    { label: 'Shots off Target', home: data.home_stats.shots_off_target, away: data.away_stats.shots_off_target },
    { label: 'Blocked Shots', home: data.home_stats.blocked_shots, away: data.away_stats.blocked_shots },
    { label: 'Corner Kicks', home: data.home_stats.corners, away: data.away_stats.corners },
    { label: 'Offsides', home: data.home_stats.offsides, away: data.away_stats.offsides },
    { label: 'Yellow Cards', home: data.home_stats.yellows, away: data.away_stats.yellows },
    { label: 'Red Cards', home: data.home_stats.reds, away: data.away_stats.reds },
    { label: 'Goalkeeper Saves', home: data.home_stats.saves, away: data.away_stats.saves },
    { label: 'Total Passes', home: data.home_stats.passes, away: data.away_stats.passes },
    { label: 'Passes Accurate', home: data.home_stats.passes_pct, away: data.away_stats.passes_pct, suffix: '%' },
    { label: 'Fouls', home: data.home_stats.fouls, away: data.away_stats.fouls },
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {stats.map((stat) => {
        const homeVal = stat.home ?? 0
        const awayVal = stat.away ?? 0
        const total = homeVal + awayVal
        const homePercent = total > 0 ? (homeVal / total) * 100 : 50
        const awayPercent = total > 0 ? (awayVal / total) * 100 : 50

        const formatValue = (val: number | null | undefined) => {
          if (val === null || val === undefined) return '-'
          return stat.decimal ? val.toFixed(2) : val.toString()
        }

        return (
          <div key={stat.label}>
            {/* Stat label centered */}
            <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
              {stat.label}
            </div>
            {/* Bar container */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {/* Home value */}
              <span style={{
                minWidth: 36,
                textAlign: 'right',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'var(--muted)'
              }}>
                {formatValue(stat.home)}{stat.suffix || ''}
              </span>
              {/* Bar area */}
              <div style={{
                flex: 1,
                display: 'flex',
                height: 8,
                borderRadius: 4,
                overflow: 'hidden',
                background: 'rgba(255,255,255,0.1)'
              }}>
                {/* Home bar (grows from right) */}
                <div style={{
                  width: `${homePercent}%`,
                  background: homeColor,
                  borderRadius: '4px 0 0 4px',
                }} />
                {/* Away bar (grows from left) */}
                <div style={{
                  width: `${awayPercent}%`,
                  background: awayColor,
                  borderRadius: '0 4px 4px 0',
                }} />
              </div>
              {/* Away value */}
              <span style={{
                minWidth: 36,
                textAlign: 'left',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'var(--muted)'
              }}>
                {formatValue(stat.away)}{stat.suffix || ''}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface H2HTabProps {
  data: HistoricalFixtureDetail
  currentFixtureId: number
  onFixtureClick: (fixtureId: number) => void
}

function H2HMatchCard({ match, currentFixtureId, onFixtureClick }: {
  match: H2HMatchStatsOut
  currentFixtureId: number
  onFixtureClick: (fixtureId: number) => void
}) {
  const badge = getLeagueBadge(match.league_name)
  const isClickable = Boolean(match.api_fixture_id && match.api_fixture_id !== currentFixtureId)
  const isCurrent = match.api_fixture_id === currentFixtureId

  const handleClick = () => {
    if (isClickable && match.api_fixture_id) {
      onFixtureClick(match.api_fixture_id)
    }
  }

  return (
    <div
      onClick={isClickable ? handleClick : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 10px',
        gap: 10,
        cursor: isClickable ? 'pointer' : 'default',
        background: isCurrent ? 'rgba(var(--accent-rgb), 0.15)' : 'rgba(255,255,255,.02)',
        borderRadius: 6,
        marginBottom: 4,
        border: isCurrent ? '1px solid var(--accent)' : '1px solid transparent',
        opacity: !match.api_fixture_id ? 0.5 : 1,
      }}
    >
      <span style={{ color: 'var(--muted)', fontSize: 11, minWidth: 70 }}>{match.date}</span>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, textAlign: 'right', flex: 1 }}>{match.home_team_name}</span>
        <span style={{ fontWeight: 700, color: 'var(--accent)', minWidth: 45, textAlign: 'center', fontSize: 13 }}>
          {match.home_score ?? '-'} - {match.away_score ?? '-'}
        </span>
        <span style={{ fontSize: 12, textAlign: 'left', flex: 1 }}>{match.away_team_name}</span>
      </div>
      <span
        title={match.league_name || 'Unknown'}
        style={{
          background: badge.color,
          color: badge.color === '#dae025' ? '#000' : '#fff',
          fontSize: 9,
          fontWeight: 700,
          padding: '2px 5px',
          borderRadius: 3,
        }}
      >
        {badge.code}
      </span>
      {isCurrent && (
        <span style={{ color: 'var(--accent)', fontSize: 10, fontWeight: 600 }}>Current</span>
      )}
      {isClickable && (
        <span style={{ color: 'var(--muted)', fontSize: 10 }}>→</span>
      )}
    </div>
  )
}

function H2HTab({ data, currentFixtureId, onFixtureClick }: H2HTabProps) {
  const [filter, setFilter] = useState<H2HFilter>('last10')

  // Get team API IDs from the fixture detail
  const homeApiId = data.home_team.api_team_id
  const awayApiId = data.away_team.api_team_id

  // Fetch H2H data using team API IDs
  const { data: h2hStats, error } = useSWR<H2HStatsResponse>(
    homeApiId && awayApiId
      ? `/fixtures/h2h-by-teams?home_team_api_id=${homeApiId}&away_team_api_id=${awayApiId}&filter=${filter}`
      : null,
    fetcher,
    { revalidateOnFocus: false }
  )

  if (!homeApiId || !awayApiId) {
    return <div className="ma-empty">Team data not available for H2H lookup</div>
  }

  if (error) {
    return <div className="ma-empty">Failed to load H2H data</div>
  }

  if (!h2hStats) {
    return <div className="ma-loading">Loading H2H history...</div>
  }

  const { matches, total_matches, available_filters } = h2hStats

  const filterLabels: Record<H2HFilter, string> = {
    last3: 'L3',
    last5: 'L5',
    last10: 'L10',
    all: 'All',
  }

  return (
    <div>
      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {available_filters.map((f) => (
          <button
            key={f}
            className={`chip ${filter === f ? 'chip-active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {filterLabels[f]}
          </button>
        ))}
      </div>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
        fontSize: '0.75rem',
        color: 'var(--muted)'
      }}>
        <span>{data.home_team.name} vs {data.away_team.name}</span>
        <span>{total_matches} total matches</span>
      </div>

      {/* Match list */}
      <div>
        {matches.map((match, idx) => (
          <H2HMatchCard
            key={match.api_fixture_id ?? `match-${idx}`}
            match={match}
            currentFixtureId={currentFixtureId}
            onFixtureClick={onFixtureClick}
          />
        ))}
      </div>

      {matches.length === 0 && (
        <div className="ma-empty">No H2H matches found</div>
      )}
    </div>
  )
}

interface StatRowProps {
  label: string
  home?: number | null
  away?: number | null
  suffix?: string
  decimal?: boolean
}

function StatRow({ label, home, away, suffix = '', decimal = false }: StatRowProps) {
  const formatValue = (val?: number | null) => {
    if (val === null || val === undefined) return '-'
    return decimal ? val.toFixed(1) : val.toString()
  }

  return (
    <div className="stat-row">
      <span className="stat-value">{formatValue(home)}{suffix && home != null ? suffix : ''}</span>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{formatValue(away)}{suffix && away != null ? suffix : ''}</span>
    </div>
  )
}

export function HistoricalFixtureModal({ fixtureId: initialFixtureId, onClose, onFixtureChange }: HistoricalFixtureModalProps) {
  const [activeTab, setActiveTab] = useState<ModalTab>('facts')
  const [currentFixtureId, setCurrentFixtureId] = useState(initialFixtureId)
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null)

  const { data, error } = useSWR<HistoricalFixtureDetail>(
    `/fixtures/${currentFixtureId}/detail`,
    fetcher,
    { revalidateOnFocus: false }
  )

  // Handle fixture change from H2H tab
  const handleFixtureChange = useCallback((newFixtureId: number) => {
    setCurrentFixtureId(newFixtureId)
    setActiveTab('facts') // Switch back to facts tab when changing fixture
    onFixtureChange?.(newFixtureId)
  }, [onFixtureChange])

  // Handle player click to show player profile
  const handlePlayerClick = useCallback((playerId: number) => {
    setSelectedPlayerId(playerId)
  }, [])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedPlayerId) {
          setSelectedPlayerId(null)
        } else {
          onClose()
        }
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, selectedPlayerId])

  // Close on overlay click
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }, [onClose])

  // Use portal to render at document body level, avoiding any stacking context issues
  return createPortal(
    <div className="historical-modal-overlay" onClick={handleOverlayClick}>
      <div className="historical-modal">
        <div className="historical-modal-header">
          {data ? (
            <div className="historical-modal-title">
              <div className="historical-modal-teams">
                {data.home_team.crest_url && <img src={data.home_team.crest_url} alt="" />}
                <span>{data.home_team.name}</span>
                <span className="historical-modal-score">{data.home_score} - {data.away_score}</span>
                <span>{data.away_team.name}</span>
                {data.away_team.crest_url && <img src={data.away_team.crest_url} alt="" />}
              </div>
              <div className="historical-modal-meta">
                {new Date(data.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                {data.league_name && ` • ${data.league_name}`}
              </div>
            </div>
          ) : (
            <div className="historical-modal-title">Loading...</div>
          )}
          <button className="historical-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="historical-modal-content">
          {error && <div className="ma-error">Failed to load fixture details</div>}
          {!data && !error && <div className="ma-loading">Loading fixture details...</div>}
          {data && (
            <>
              <ModalTabNav activeTab={activeTab} onTabChange={setActiveTab} />
              {activeTab === 'facts' && <FactsTab data={data} onPlayerClick={handlePlayerClick} />}
              {activeTab === 'lineup' && <LineupTab data={data} onPlayerClick={handlePlayerClick} />}
              {activeTab === 'stats' && <StatsTab data={data} />}
              {activeTab === 'h2h' && (
                <H2HTab
                  data={data}
                  currentFixtureId={currentFixtureId}
                  onFixtureClick={handleFixtureChange}
                />
              )}
            </>
          )}
          {/* Player Profile Modal */}
          {selectedPlayerId && (
            <PlayerProfileModal
              playerId={selectedPlayerId}
              onClose={() => setSelectedPlayerId(null)}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
